import { Injectable } from '@angular/core';

/**
 * NAV-03 (Phase 32 — landing model): maps a user's platform_roles claim to the
 * default surface they land on after sign-in. Consumed by the bare `''` route's
 * canActivate resolver (see app.routes.ts), which supplies UserService.roles().
 *
 * LANDING MODEL (Architect 2026-06-21, UI-SPEC §Landing/Home Contract):
 * post-login landing is the role-aware **Home/Dashboard** for ALL provisioned
 * roles — including VIEWER (which previously landed on a read Records view). The 7
 * built role dashboard sub-components are the live Home. Per-surface routing now
 * happens from the Home next-action card and the Records nav resolver
 * (RecordsLandingService.recordsRouteFor) — NOT from landing. So every role
 * resolves to `/dashboard`; the empty-roles case keeps the same fallback.
 *
 * Kept a PURE function of its argument (no UserService injection) so the spec can
 * drive it with literal role arrays. Matching is case-insensitive to mirror
 * UserService.hasAnyRole's lowercasing tolerance.
 */
@Injectable({ providedIn: 'root' })
export class RoleLandingService {
  /** The role-aware Home/Dashboard — the single post-login landing for every role. */
  private static readonly HOME = '/dashboard';

  /**
   * Resolve the default landing route for the supplied roles.
   *
   * NAV-03: every provisioned (and even unknown / no-roles) user lands on the
   * role-aware Home/Dashboard. DashboardComponent picks the correct role
   * sub-component, and each Home surfaces a next-action to the user's primary
   * surface. This intentionally no longer routes any role directly to a Records
   * View Screen — that is reached from the Home next-action and the Records nav
   * item (RecordsLandingService).
   *
   * @param roles the user's platform_roles claim values (any casing). Unused for
   *   routing now (all roles land Home) but retained for signature stability with
   *   the `''` resolver and for future per-role landing tweaks.
   * @returns the absolute Home/Dashboard route path.
   */
  defaultRouteFor(roles: readonly string[]): string {
    void roles;
    return RoleLandingService.HOME;
  }
}
